import React, { useState, useEffect } from 'react';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';

/**
 * Reusable phone-number input with country-code selector.
 *
 * Props:
 *  - value             : string in E.164 format (e.g. "+447712345678")
 *  - onChange(v)       : called with the new value (or undefined when cleared)
 *  - required          : whether the field must be filled (default true)
 *  - placeholder       : input placeholder (default "Phone number")
 *  - testIdPrefix      : prefix for data-testid attributes
 *  - className         : extra classes for the wrapper
 *  - quickPickCountries: array of ISO country codes shown as chips above the
 *                        input for one-click selection. Default ['GB','US'] —
 *                        the gallery's two largest buyer markets. Pass [] to
 *                        hide the row entirely.
 *
 * Styling: Swiss-minimalist — neutral border, focus on black. No country
 * is pre-selected; the chips and dropdown let the user pick themselves.
 */
const COUNTRY_META = {
  GB: { flag: '🇬🇧', label: 'UK' },
  US: { flag: '🇺🇸', label: 'US' },
  IE: { flag: '🇮🇪', label: 'Ireland' },
  FR: { flag: '🇫🇷', label: 'France' },
  DE: { flag: '🇩🇪', label: 'Germany' },
  AU: { flag: '🇦🇺', label: 'Australia' },
  CA: { flag: '🇨🇦', label: 'Canada' },
};

export default function PhoneField({
  value,
  onChange,
  required = true,
  placeholder = 'Phone number',
  testIdPrefix = 'phone',
  className = '',
  quickPickCountries = ['GB', 'US'],
}) {
  const [country, setCountry] = useState();

  // If the value already has a country code, keep our local state in sync so
  // the chip's "active" highlight reflects reality.
  useEffect(() => {
    if (!value) return;
    // E.164 prefixes for the chip countries we care about
    const prefixMap = { '+44': 'GB', '+1': 'US', '+353': 'IE', '+33': 'FR', '+49': 'DE', '+61': 'AU' };
    for (const [prefix, iso] of Object.entries(prefixMap)) {
      if (value.startsWith(prefix)) {
        setCountry(iso);
        return;
      }
    }
  }, [value]);

  const pick = (iso) => {
    setCountry(iso);
    // Clearing first ensures the dialing prefix gets re-applied cleanly.
    onChange('');
  };

  return (
    <div className={`zog-phone-field ${className}`} data-testid={`${testIdPrefix}-wrapper`}>
      {quickPickCountries.length > 0 && (
        <div className="flex items-center gap-2 mb-2" data-testid={`${testIdPrefix}-quickpick`}>
          <span className="text-[11px] uppercase tracking-[0.18em] text-neutral-500">Calling from</span>
          {quickPickCountries.map((iso) => {
            const meta = COUNTRY_META[iso];
            if (!meta) return null;
            const active = country === iso;
            return (
              <button
                key={iso}
                type="button"
                onClick={() => pick(iso)}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs border transition-colors ${
                  active
                    ? 'bg-black text-white border-black'
                    : 'bg-white text-neutral-700 border-neutral-300 hover:border-black'
                }`}
                data-testid={`${testIdPrefix}-quickpick-${iso.toLowerCase()}`}
                aria-pressed={active}
              >
                <span aria-hidden="true">{meta.flag}</span>
                <span>{meta.label}</span>
              </button>
            );
          })}
        </div>
      )}

      <PhoneInput
        international
        addInternationalOption
        country={country}
        onCountryChange={setCountry}
        value={value || ''}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        numberInputProps={{
          'data-testid': `${testIdPrefix}-input`,
          className: 'zog-phone-input',
        }}
      />
    </div>
  );
}

export const isPhoneValid = (v) => Boolean(v && isValidPhoneNumber(v));
